import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

const SidebarContext = React.createContext(null);

const SidebarProvider = ({
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  className,
  style,
  children,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const setOpen = isControlled ? onOpenChange : setUncontrolledOpen;

  const value = React.useMemo(
    () => ({
      open,
      setOpen,
      state: open ? "expanded" : "collapsed",
      toggleSidebar: () => setOpen?.(!open),
    }),
    [open, setOpen],
  );

  return (
    <SidebarContext.Provider value={value}>
      <div className={cn("relative w-full", className)} style={style}>
        {children}
      </div>
    </SidebarContext.Provider>
  );
};

const useSidebar = () => {
  const ctx = React.useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return ctx;
};

const Sidebar = React.forwardRef(
  (
    { side = "left", collapsible = "icon", className, children, ...props },
    ref,
  ) => {
    const { open, state } = useSidebar();

    return (
      <div
        ref={ref}
        data-side={side}
        data-state={state}
        data-collapsible={collapsible}
        className={cn(
          "group/sidebar fixed inset-y-0 z-40 hidden flex-col h-svh border-r border-gray-200 bg-white transition-[width] duration-200 ease-linear dark:border-slate-700 dark:bg-slate-800 lg:flex",
          side === "left" ? "left-0" : "right-0",
          open ? "w-64" : "w-16",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Sidebar.displayName = "Sidebar";

const SidebarHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-shrink-0 sticky top-0 z-10 flex items-center gap-2 border-b border-gray-200 bg-white/95 px-3 py-3 backdrop-blur dark:border-slate-700 dark:bg-slate-800/95",
      className,
    )}
    {...props}
  />
));
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-shrink-0 overflow-y-auto border-t border-gray-200 bg-white/95 px-3 py-3 backdrop-blur dark:border-slate-700 dark:bg-slate-800/95",
      className,
    )}
    {...props}
  />
));
SidebarFooter.displayName = "SidebarFooter";

const SidebarContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 overflow-y-auto px-2 py-3", className)}
    {...props}
  />
));
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("space-y-2", className)} {...props} />
));
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "px-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400",
      className,
    )}
    {...props}
  />
));
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("space-y-1", className)} {...props} />
));
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn("space-y-1", className)} {...props} />
));
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("group/menu-item", className)} {...props} />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

const SidebarMenuButton = React.forwardRef(
  ({ className, asChild = false, isActive = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-active={isActive ? "true" : "false"}
        className={cn(
          "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
          "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-700",
          "data-[active=true]:bg-blue-600 data-[active=true]:text-white dark:data-[active=true]:bg-blue-900/30 dark:data-[active=true]:text-blue-300",
          "group-data-[state=collapsed]/sidebar:justify-center",
          className,
        )}
        {...props}
      />
    );
  },
);
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuSub = React.forwardRef(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn("mt-1 space-y-1 pl-9", className)} {...props} />
));
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = React.forwardRef(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("group/menu-sub-item", className)} {...props} />
));
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = React.forwardRef(
  ({ className, asChild = false, isActive = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-active={isActive ? "true" : "false"}
        className={cn(
          "flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors",
          "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-700",
          "data-[active=true]:bg-blue-600 data-[active=true]:text-white dark:data-[active=true]:bg-blue-900/30 dark:data-[active=true]:text-blue-300",
          className,
        )}
        {...props}
      />
    );
  },
);
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

const SidebarTrigger = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const { toggleSidebar } = useSidebar();
    return (
      <button
        ref={ref}
        type="button"
        onClick={toggleSidebar}
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
SidebarTrigger.displayName = "SidebarTrigger";

export {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarFooter,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarTrigger,
  useSidebar,
};
